import { AxiosResponse } from 'axios';
import { LdJsonBreadCrumbsResort } from 'components/LdJson';
import { TerrainOverview } from 'components/resort/TerrainDetailed';
import { TrailMap } from 'components/resort/TrailMap';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React from 'react';
import { Routes } from 'routes';
import { getResortSlug } from 'util/memcache';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchResortAccommodations,
  fetchResortDescriptionTabs, fetchResortRelated,
  fetchResortRelatedNews,
  fetchResortShortInfo, fetchResortShortWeatherReport,
  fetchResortTrailMap,
} from 'api/resort/static';
import {
  IDescriptionsResort,
  IRelatedNews,
  IResortPageInfo,
  IResortPathInfo,
  IResortTrailmapPage,
} from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { RegularAd } from 'googlead/components/RegularAd';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { TotalLifts } from 'components/resort/TotalLifts';
import { AiAssistantWidget } from 'components/common/AiAssistantWidget';
import styles from './trailmap.module.scss';

export type ResortTrailmapPageProps = {
  resort: IResortPageInfo,
  trailmap: IResortTrailmapPage | null,
  pathInfo: IResortPathInfo,
  resortDescriptionTabs: Array<IDescriptionsResort>,
  relatedNews: IRelatedNews | null,
  accommodations: IAccommodationPage | null,

} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-trailmap-page';
export const TEMPLATE_ID = '3.2.3';
export const TEMPLATE_NAME = 'Resort Trailmap';
export default function ResortTrailmapPage({
  resort,
  trailmap,
  pathInfo,
  resortDescriptionTabs,
  relatedNews,
}: ResortTrailmapPageProps) {
  const resortCountry = resort?.region?.country_code;

  if (!resort) {
    return <Hero400Error light />;
  }

  return (
    <>
      <Main>
        {trailmap && (
          <TrailMap
            isFresh
            trailMap={trailmap.trailMap}
            resort={pathInfo}
            descriptionTabs={resortDescriptionTabs}
          />
        )}

        {trailmap && (
          <div className={styles.wrapper}>
            <TerrainOverview
              isFresh
              data={trailmap}
              pathInfo={pathInfo}
              resortCountry={resortCountry}
            />
            <TotalLifts lifts={trailmap.lifts} />
          </div>
        )}
        <AiAssistantWidget
          templateId={TEMPLATE_ID}
          name={resort.title}
          margin={{ marginTop: '0rem', marginBottom: '2rem' }}
        />
        <PlanYourTrip
          resort={pathInfo}
          hasSkiRental={pathInfo.hasSkiRental}
          hasFlights={pathInfo.hasFlights}
          hasCarRental={pathInfo.hasCarRental}
          liftsUrl={resort.liftsUrl}
          rentalUrl={resort.rentalUrl}
          lessonsUrl={resort.lessonsUrl}
          isPartner={resort.isPartner}
          resortCountry={resortCountry}
        />
        <RegularAd isFirst />
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd id={pathInfo.uuid} />
      </Side>
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={resort.region}
        fn={Routes.resortTripPlanningTrailMap}
      />
    </>
  );
}

export const fetchProps = async ({ resort }: {
  region: string, resort: string
}): Promise<ResortTrailmapPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const resortDescriptionTabs = await fetchResortDescriptionTabs(uuid);
  const relatedNews = await fetchResortRelatedNews(uuid);

  return {
    type: 'resort',
    route: 'trailmap',
    pathInfo: cache.data,
    resortDescriptionTabs,
    resort: await fetchResortShortInfo(uuid),
    shortWeather: await fetchResortShortWeatherReport(uuid),
    trailmap: await fetchResortTrailMap(uuid),
    relatedNews,

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.TRAILMAP,
      tab: CurrentTab.TRIP_PLANNING,
      head: HeaderNavCurLink.TRIP_PLANNING,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort trailmap',
    hero: {
      title: 'resort.trailMap.heroTitle',
      showVideo: false,
    },
    meta: {
      description: 'Resort.trailmap.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.trailmap.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
    accommodations: await fetchResortAccommodations(uuid),

  };
};

// This function gets called at build time
export const getResortTrailmapPageStaticProps = wrapStaticProps(fetchProps);

export const getResortTrailmapPageServerSideProps = wrapServerSideProps(fetchProps);
